import { useRef, useEffect, useState, useMemo } from "react";

export function TableOperacionalFlow({ dataTable, year, month }) {
  const tableRef = useRef(null);

  const [saldoAcumulado, setSaldoAcumulado] = useState({});

  const monthsMap = [
    { value: 1, label: "Jan" },
    { value: 2, label: "Fev" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Abr" },
    { value: 5, label: "Mai" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Ago" },
    { value: 9, label: "Set" },
    { value: 10, label: "Out" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dez" },
  ];

  const daysInMonth = month ? new Date(year, month, 0).getDate() : null;
  const daysArray = month
    ? Array.from({ length: daysInMonth }, (_, i) => i + 1)
    : null;

  const columns = month ? daysArray : monthsMap.map((m) => m.label); 

  function isParent(name) {
    const numericPart = name.split(" - ")[0];
    const dotCount = (numericPart.match(/\./g) || []).length;
    return dotCount < 2;
  }

  function calculateParentValues(dataTable) {
    const dataMap = new Map();

    const updatedData = dataTable.map((item) => ({
      ...item,
      values: { ...item.values },
    }));

    updatedData.forEach((item) => dataMap.set(item.name, item));

    updatedData.forEach((item) => {
      const numericPart = item.name.split(" - ")[0];
      const dotCount = (numericPart.match(/\./g) || []).length;

      if (dotCount < 2) {
        item.values = {}; 
      }
    });

    updatedData.forEach((child) => {
      const numericPart = child.name.split(" - ")[0];
      const dotCount = (numericPart.match(/\./g) || []).length;

      if (dotCount > 0) {
        const subParentKey = numericPart.substring(
          0,
          numericPart.lastIndexOf(".")
        );
        const parentKey = subParentKey.includes(".")
          ? subParentKey.substring(0, subParentKey.lastIndexOf("."))
          : subParentKey;

        const subParent = updatedData.find((p) =>
          p.name.startsWith(`${subParentKey} -`)
        );
        const parent = updatedData.find((p) =>
          p.name.startsWith(`${parentKey} -`)
        );

        if (subParent) {
          Object.entries(child.values || {}).forEach(([key, value]) => {
            subParent.values[key] = (subParent.values[key] || 0) + value;
          });
        }

        if (parent) {
          Object.entries(child.values || {}).forEach(([key, value]) => {
            parent.values[key] = (parent.values[key] || 0) + value;
          });
        }
      }
    });

    return updatedData;
  }

  function calculateTotalValues(dataTable, columns, isMonthlyView) {
    const totalEntries = {};
    const totalExits = {}; 
    const totalBalance = {}; 

    columns.forEach((col) => {
      totalEntries[col] = 0;
      totalExits[col] = 0;
      totalBalance[col] = 0;
    });

    const monthMap = {
      1: "Jan",
      2: "Fev",
      3: "Mar",
      4: "Abr",
      5: "Mai",
      6: "Jun",
      7: "Jul",
      8: "Ago",
      9: "Set",
      10: "Out",
      11: "Nov",
      12: "Dez",
    };

    dataTable.forEach((item) => {
      Object.entries(item.values).forEach(([key, value]) => {
        let targetColumn;

        if (isMonthlyView) {
          targetColumn = monthMap[key];
        } else {
          targetColumn = key;
        }

        if (targetColumn && totalEntries[targetColumn] !== undefined) {
          if (item.category === "Entrada") {
            totalEntries[targetColumn] += value;
          } else if (item.category === "Saída") {
            totalExits[targetColumn] += value;
          }
        }
      });
    });

    let accumulatedBalance = 0;
    columns.forEach((column) => {
      const entryValue = totalEntries[column] || 0;
      const exitValue = totalExits[column] || 0;
      accumulatedBalance = accumulatedBalance + entryValue - exitValue;
      totalBalance[column] = accumulatedBalance;
    });

    return { totalEntries, totalExits, totalBalance }; 
  }

  useEffect(() => {
    if (dataTable.length > 0) {
      const isMonthlyView = !month; 

      const { totalEntries, totalExits, totalBalance } = calculateTotalValues(
        dataTable,
        columns,
        isMonthlyView
      );
      setSaldoAcumulado(totalBalance); 
    }
  }, [dataTable, columns, month]);

const updatedDataTable = useMemo(() => calculateParentValues(dataTable), [dataTable]);
  return (
    <div
      style={{
        overflowX: "scroll",
      }}
    >
      <table ref={tableRef} className="" style={{width: "100%"}}>
        <thead>
          <tr>
            <th colSpan={columns.length + 1} className="text-center p-2">
              Ano de Referência: {year}
            </th>
          </tr>

          {month && (
            <tr>
              <th colSpan={columns.length + 1} className="text-center  p-2">
                Mês de Referência:{" "}
                {monthsMap.find((m) => m.value === month)?.label ||
                  "Mês Inválido"}
              </th>
            </tr>
          )}

          <tr>
            <th className="text-center border p-2">Centro de Custo</th>
            {columns.map((col, index) => (
              <th key={index} className="text-center border p-1">
                {col}
              </th>
            ))}
          </tr>
          <tr style={{}}>
            <td className="text-center border">Saldo Inicial</td>
            {columns.map((col, i) => (
              <td key={i} className="text-center border p-1">
                {saldoAcumulado[col] !== undefined
                  ? `R$${Number(saldoAcumulado[col]).toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}`
                  : ""}
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {["Entrada", "Saída"].map((category) => (
            <>
              <tr 
                className=" border fw-bold"
                style={{ fontWeight: "bold", backgroundColor: "#cccccc", color: "#000" }}
              >
                <td
                  colSpan={columns.length + 1}
                  className="text-center border p-2"
                >
                  {category}
                </td>
              </tr>

              {updatedDataTable
                .filter(
                  (item) => item.category === category && item.name !== "Total"
                ) 
                .map((item, index) => {
                  const isParentItem = isParent(item.name);
                  return (
                    <tr key={index}>
                      <td
                        className="text-center border p-1"
                        style={{
                          fontWeight: isParentItem ? "bold" : "normal",
                        }}
                      >
                        {item.name}
                      </td>
                      {columns.map((col, idx) => {
                        let value;
                        if (month) {
                          value = item.values[col] || "-";
                        } else {
                          const monthIndex = monthsMap.find(
                            (m) => m.label === col
                          )?.value;
                          value = item.values[monthIndex] || "-";
                        }

                        return (
                          <td key={idx} className="text-center border p-1">
                            {value !== "-"
                              ? `R$${Number(value).toLocaleString("pt-BR", {
                                  minimumFractionDigits: 2,
                                })}`
                              : ""}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}
